<template>
  <div class="sales-closures-table">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center p-8">
      <div class="text-center">
        <div class="spinner mb-2">
          <div class="spinner-border" role="status">
            <span class="sr-only">Cargando...</span>
          </div>
        </div>
        <p class="text-sm text-gray-500">Cargando cierres de ventas...</p>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!closures || closures.length === 0" class="text-center p-8 text-gray-500">
      <p>No hay cierres de ventas registrados</p>
      <vs-button class="mt-4" :disabled="isOpeningForm" @click="openCreateModal">
        <i v-if="isOpeningForm" class="spinner-border-button mr-2" role="status" aria-hidden="true"></i>
        <i v-else class="feather icon-plus"></i>
        {{ isOpeningForm ? 'Cargando...' : 'Crear primer cierre' }}
      </vs-button>
    </div>

    <!-- Table Content -->
    <div v-else class="space-y-4">
      <!-- Closures Table -->
      <div class="overflow-x-auto rounded-lg border border-gray-200">
        <table class="w-full text-sm">
          <thead class="bg-grey-light">
            <tr>
              <th class="px-4 py-3 text-left font-semibold">#</th>
              <th class="px-4 py-3 text-left font-semibold">Estado</th>
              <th class="px-4 py-3 text-left font-semibold">Empresa</th>
              <th class="px-4 py-3 text-left font-semibold">Modo Fact.</th>
              <th class="px-4 py-3 text-left font-semibold">Fechas</th>
              <th class="px-4 py-3 text-right font-semibold">Monto Original</th>
              <th v-if="isMultiCurrency" class="px-4 py-3 text-right font-semibold">Monto USD</th>
              <th class="px-4 py-3 text-left font-semibold">Autor</th>
              <th class="px-4 py-3 text-center font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="closure in closures"
              :key="closure.id"
              :class="getRowClasses(closure)"
            >
              <!-- Closure Number -->
              <td class="px-4 py-3 font-semibold">{{ getClosureDisplayNumber(closure) }}</td>

              <!-- Status Badge -->
              <td class="px-4 py-3">
                <div class="flex items-center gap-1">
                  <vs-chip
                    :color="CLOSURE_STATUS_COLORS[closure.status]"
                    class="text-white"
                  >
                    {{ CLOSURE_STATUS_LABELS[closure.status] }}
                    <template v-if="closure.status === 'draft'" #icon>
                      <i class="feather icon-alert-circle"></i>
                    </template>
                  </vs-chip>
                  <feather-icon
                    v-if="closure.status === 'canceled' && closure.cancellation_comment"
                    icon="InfoIcon"
                    svgClasses="w-4 h-4 text-danger cursor-pointer flex-shrink-0"
                    title="Ver motivo de anulación"
                    @click.stop="openReasonModal(closure.cancellation_comment)"
                  />
                </div>
              </td>

              <!-- Company (Legal Name) -->
              <td class="px-4 py-3 text-sm font-medium text-gray-700">
                {{ getClosureLegalName(closure) }}
              </td>

              <!-- Billing Mode -->
              <td class="px-4 py-3 text-xs">
                {{ BILLING_MODES_LABELS[closure.billing_mode] }}
              </td>

              <!-- Date Range -->
              <td class="px-4 py-3 whitespace-nowrap">
                {{ dateFormatter(closure.date_from) }} - {{ dateFormatter(closure.date_to) }}
              </td>

              <!-- Original Amount (with currency) -->
              <td class="px-4 py-3 text-right font-semibold text-latinad">
                {{ stringCurrencyFormatter(closure.amount) }} {{ closure.currency }}
              </td>

              <!-- Amount USD (if multi-currency) -->
              <td v-if="isMultiCurrency" class="px-4 py-3 text-right font-semibold text-latinad">
                {{ stringCurrencyFormatter(closure.amount_usd || closure.amount) }} USD
              </td>

              <!-- Author -->
              <td class="px-4 py-3 text-sm">
                <span v-if="closure.author" class="text-gray-700">
                  {{ closure.author.first_name }} {{ closure.author.last_name }}
                  <span class="text-gray-500 text-xs">({{ closure.author.email }})</span>
                </span>
                <span v-else class="text-gray-400 italic">Sin información</span>
              </td>

              <!-- Actions -->
              <td class="px-4 py-3">
                <div class="flex justify-center gap-2">
                  <!-- View Details Button -->
                  <vs-button
                    v-if="closure.status === 'canceled'"
                    icon
                    size="small"
                    class="bg-info text-white hover:bg-information"
                    @click="viewClosure(closure)"
                    :title="`Ver detalles del cierre ${getClosureDisplayNumber(closure)}`"
                  >
                    <i class="feather icon-eye"></i>
                  </vs-button>

                  <!-- Edit Button (only if not canceled) -->
                  <vs-button
                    v-if="closure.status !== 'canceled'"
                    icon
                    size="small"
                    class="bg-warning text-white hover:bg-yellow-600"
                    @click="editClosure(closure)"
                    :title="`Editar cierre ${getClosureDisplayNumber(closure)}`"
                  >
                    <i class="feather icon-edit"></i>
                  </vs-button>

                  <!-- Cancel Button (only if not already canceled and no active billing) -->
                  <vs-button
                    v-if="closure.status !== 'canceled'"
                    icon
                    size="small"
                    class="bg-danger text-white hover:bg-red-600"
                    :disabled="hasBillingActive(closure)"
                    @click="cancelClosure(closure)"
                    :title="hasBillingActive(closure) ? 'No se puede anular: tiene una facturación activa.' : `Anular cierre ${getClosureDisplayNumber(closure)}`"
                  >
                    <i class="feather icon-trash-2"></i>
                  </vs-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Totals Section -->
      <div class="p-3 px-4 flex justify-between items-center bg-grey-light rounded-lg mt-4">
        <span class="text-sm font-semibold">
          Total USD:
        </span>
        <span class="font-bold text-latinad">
          {{ stringCurrencyFormatter(currencyTotals.byCurrency['USD']) }} USD
        </span>
      </div>

      <!-- Create New Closure Button -->
      <vs-button
        class="mt-6 w-full bg-latinad text-white hover:bg-latinad-light"
        :disabled="isOpeningForm"
        @click="openCreateModal"
      >
        <i v-if="isOpeningForm" class="spinner-border-button mr-2" role="status" aria-hidden="true"></i>
        <i v-else class="feather icon-plus-circle mr-2"></i>
        {{ isOpeningForm ? 'Cargando...' : 'Crear nuevo cierre' }}
      </vs-button>
    </div>

    <!-- Detail Modal -->
    <sales-closures-details
      v-if="selectedClosure"
      :active.sync="isDetailModalOpen"
      :closure="selectedClosure"
      @close="handleDetailClose"
    />

    <!-- Create/Edit Modal -->
    <sales-closures-form
      :active.sync="isFormModalOpen"
      :campaign-id="campaignId"
      :closure="editingClosure"
      :existing-closures="closures"
      @loading-change="handleFormLoadingChange"
      @save="handleSave"
      @cancel="handleFormCancel"
    />

    <!-- Cancel closure modal (mandatory reason) -->
    <vs-popup title="" class="cancel-closure-modal" :active.sync="cancelModalActive" @close="closeCancelModal">
      <div class="cancel-closure-body">
        <button class="popup-close" @click="closeCancelModal">✕</button>
        <div class="cancel-closure-icon">
          <feather-icon icon="AlertTriangleIcon" svgClasses="w-8 h-8" />
        </div>
        <h3 class="cancel-closure-title">Anular cierre</h3>
        <p class="cancel-closure-message">
          ¿Está seguro de que desea anular el cierre #{{ closureToCancel ? getClosureDisplayNumber(closureToCancel) : '' }}?<br>
          Esta acción no se puede deshacer.
        </p>
        <div class="cancel-closure-field">
          <label class="cancel-closure-label">Comentario</label>
          <vs-textarea
            v-model="cancelComment"
            :rows="3"
            maxlength="1000"
            placeholder="Indique el motivo de anulación, campo obligatorio"
          />
          <p v-if="cancelComment.trim().length > 0 && cancelComment.trim().length < 10" class="cancel-closure-hint">
            Mínimo 10 caracteres.
          </p>
        </div>
        <div class="cancel-closure-actions">
          <vs-button type="border" color="dark" @click="closeCancelModal">Cancelar</vs-button>
          <vs-button
            color="danger"
            :disabled="cancelComment.trim().length < 10 || isCancelingClosure"
            @click="confirmCancelClosure"
          >
            {{ isCancelingClosure ? 'Anulando...' : 'Anular' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Cancellation reason modal -->
    <comment-modal
      class="reason-modal-zfix"
      :modalActive.sync="reasonModalActive"
      :comment="reasonModalText"
      title="Motivo de anulación"
    />
  </div>
</template>

<style scoped src="./SalesClosuresTable.scoped.css"></style>

<style src="./SalesClosuresTable.global.css"></style>

<script>
import {
  CLOSURE_STATUS,
  CLOSURE_STATUS_LABELS,
  CLOSURE_STATUS_COLORS,
  BILLING_MODES_LABELS
} from '@/constants/salesClosures'
import { stringCurrencyFormatter, dateFormatter } from '@/utils/strings'
import { getMultiCurrencyTotals } from '@/utils/salesClosuresCalculator'
import appConfig from '@/../appConfig.js'

import SalesClosuresForm from './SalesClosuresForm.vue'
import SalesClosuresDetails from './SalesClosuresDetails.vue'
import CommentModal from '../../comment-modal/CommentModal.vue'

export default {
  name: 'SalesClosuresTable',
  components: {
    SalesClosuresForm,
    SalesClosuresDetails,
    CommentModal
  },
  props: {
    campaignId: {
      type: [String, Number],
      required: true
    },
    totalIncome: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      closures: [],
      backendTotals: null,
      isLoading: false,
      isOpeningForm: false,
      isFormModalOpen: false,
      isDetailModalOpen: false,
      selectedClosure: null,
      editingClosure: null,
      cancelModalActive: false,
      closureToCancel: null,
      cancelComment: '',
      isCancelingClosure: false,
      reasonModalActive: false,
      reasonModalText: '',
      campaignCompanyInfo: {
        fantasyName: '',
        legalName: ''
      },
      isCampaignCompanyInfoLoaded: false,
      // Constants for template
      CLOSURE_STATUS,
      CLOSURE_STATUS_LABELS,
      CLOSURE_STATUS_COLORS,
      BILLING_MODES_LABELS,
      stringCurrencyFormatter,
      dateFormatter
    }
  },
  computed: {
    currencyTotals() {
      // Use backend totals as source of truth for converted amounts
      // Backend provides authoritative conversion rates and total USD amount
      if (this.backendTotals) {
        const currencies = this.backendTotals.currencies || []
        const byCurrency = {}

        if (this.backendTotals.is_multi_currency) {
          // Multi-currency scenario: calculate original amounts per currency
          // and use backend's total_amount_usd for the converted total
          currencies.forEach(currency => {
            // Sum original amounts for each currency from active (non-canceled) closures
            byCurrency[currency] = this.closures
              .filter(c => c.status !== 'canceled' && c.currency === currency)
              .reduce((sum, c) => sum + (c.amount || 0), 0)
          })

          // Override USD total with backend's converted total (all amounts in USD)
          // This ensures conversion rate consistency across the application
          byCurrency['USD'] = this.backendTotals.total_amount_usd || 0
        } else {
          // Single currency scenario: sum amounts per currency
          currencies.forEach(currency => {
            byCurrency[currency] = this.closures
              .filter(c => c.status !== 'canceled' && c.currency === currency)
              .reduce((sum, c) => sum + (c.amount || 0), 0)
          })
        }

        return {
          byCurrency,
          isMultiCurrency: this.backendTotals.is_multi_currency || false,
          currencies: currencies.sort()
        }
      }

      // Fallback: calculate totals locally when backend totals unavailable
      return getMultiCurrencyTotals(this.closures)
    },
    isMultiCurrency() {
      return this.currencyTotals.isMultiCurrency
    }
  },
  watch: {
    campaignId(id) {
      if (id) {
        this.fetchClosures()
      }
    }
  },
  mounted() {
    // Load on mount if campaignId exists
    if (this.campaignId) {
      this.fetchClosures()
    }
  },
  methods: {
    /**
     * Fetch all sales closures for the campaign
     */
    async fetchClosures() {
      if (!this.campaignId) {
        console.warn('[SalesClosuresTable] campaignId is missing')
        this.isLoading = false
        return
      }

      this.isLoading = true
      try {
        const response = await this.$http.get(
          `${appConfig.apiUrl}/campaigns/${this.campaignId}/sales-closures`
        )

        // Handle both response formats
        let data = response.data
        let closuresData = data

        if (data && typeof data === 'object' && Array.isArray(data.data)) {
          closuresData = data.data
          // Store backend totals if available
          if (data.totals) {
            this.backendTotals = data.totals
          }
        } else if (!Array.isArray(data)) {
          closuresData = []
        }

        this.closures = closuresData

        // Keep cached company info for detail fallback fields
        await this.ensureCampaignCompanyInfo()
      } catch (error) {
        console.error('[SalesClosuresTable] Error:', error)
        this.closures = []
        this.backendTotals = null
      } finally {
        this.isLoading = false
      }
    },

    async ensureCampaignCompanyInfo() {
      if (!this.campaignId || this.isCampaignCompanyInfoLoaded) return

      try {
        const response = await this.$http.get(
          `${appConfig.apiUrl}/campaigns/${this.campaignId}/sales-closures/modal-data`
        )

        const payload = (response && response.data && response.data.data) || response.data || {}
        const company = payload.company || {}

        this.campaignCompanyInfo = {
          fantasyName: company.fantasy_name || '',
          legalName: company.legal_name || ''
        }
        this.isCampaignCompanyInfoLoaded = true
      } catch (error) {
        console.warn('[SalesClosuresTable] Could not load campaign company info for details fallback', error)
      }
    },

    /**
     * Open form modal for creating new closure
     */
    openCreateModal() {
      if (this.isOpeningForm) return
      this.editingClosure = null
      this.isOpeningForm = true
      this.isFormModalOpen = true
    },

    /**
     * Open form modal for editing closure
     */
    editClosure(closure) {
      if (this.isOpeningForm) return
      this.editingClosure = {
        ...closure,
        number: this.getClosureDisplayNumber(closure)
      }
      this.isOpeningForm = true
      this.isFormModalOpen = true
    },

    handleFormLoadingChange(isLoadingForm) {
      this.isOpeningForm = Boolean(isLoadingForm)
    },

    /**
     * View closure details
     */
    async viewClosure(closure) {
      if (
        !closure.company_fantasy_name &&
        !closure.fantasy_name &&
        !(closure.company && closure.company.fantasy_name)
      ) {
        await this.ensureCampaignCompanyInfo()
      }

      this.selectedClosure = {
        ...closure,
        company_fantasy_name:
          closure.company_fantasy_name ||
          closure.fantasy_name ||
          (closure.company && closure.company.fantasy_name) ||
          this.campaignCompanyInfo.fantasyName ||
          '',
        company_legal_name:
          closure.company_legal_name ||
          closure.legal_name ||
          closure.billing_information_name ||
          (closure.billing_information && closure.billing_information.name) ||
          this.campaignCompanyInfo.legalName ||
          ''
      }
      this.isDetailModalOpen = true
    },

    getClosureDisplayNumber(closure) {
      return (closure && (closure.number || closure.closure_number || closure.id)) || '-'
    },

    getClosureFantasyName(closure) {
      return (
        (closure && closure.company_fantasy_name) ||
        (closure && closure.fantasy_name) ||
        (closure && closure.company && closure.company.fantasy_name) ||
        '-'
      )
    },

    getClosureLegalName(closure) {
      return (
        (closure && closure.company_legal_name) ||
        (closure && closure.legal_name) ||
        (closure && closure.billing_information_name) ||
        (closure && closure.billing_information && closure.billing_information.name) ||
        '-'
      )
    },

    /**
     * Check if closure has active billing (billing_status === 'billed')
     * CA5 Requirement: Cannot cancel a closure that has active billing
     */
    hasBillingActive(closure) {
      return closure && closure.billing_status === 'billed'
    },

    /**
     * Open the cancellation modal that asks for the mandatory reason
     */
    cancelClosure(closure) {
      this.closureToCancel = closure
      this.cancelComment = ''
      this.cancelModalActive = true
    },

    closeCancelModal() {
      this.cancelModalActive = false
      this.closureToCancel = null
      this.cancelComment = ''
    },

    openReasonModal(comment) {
      this.reasonModalText = comment
      this.reasonModalActive = true
    },

    /**
     * Cancel a closure (logical delete) with the mandatory reason (min 10 chars)
     */
    async confirmCancelClosure() {
      if (!this.closureToCancel || this.cancelComment.trim().length < 10) return

      this.isCancelingClosure = true
      try {
        await this.$http.delete(
          `${appConfig.apiUrl}/campaigns/${this.campaignId}/sales-closures/${this.closureToCancel.id}`,
          { data: { cancellation_comment: this.cancelComment.trim() } }
        )

        this.$vs.notify({
          title: 'Éxito',
          text: `Cierre #${this.getClosureDisplayNumber(this.closureToCancel)} anulado correctamente`,
          color: 'success'
        })

        this.cancelModalActive = false
        this.fetchClosures()
      } catch (error) {
        console.error('Error canceling closure:', error)
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'No se pudo anular el cierre',
          color: 'danger'
        })
      } finally {
        this.isCancelingClosure = false
      }
    },

    /**
     * Handle successful save from form modal
     */
    handleSave() {
      this.isFormModalOpen = false
      this.isOpeningForm = false
      this.editingClosure = null
      this.fetchClosures()
    },

    /**
     * Handle form modal cancel
     */
    handleFormCancel() {
      this.isFormModalOpen = false
      this.isOpeningForm = false
      this.editingClosure = null
    },

    /**
     * Handle detail modal close
     */
    handleDetailClose() {
      this.isDetailModalOpen = false
      this.selectedClosure = null
    },

    /**
     * Get row CSS classes based on closure status
     */
    getRowClasses(closure) {
      return {
        'draft-status': closure.status === 'draft',
        'canceled-status': closure.status === 'canceled'
      }
    }
  }
}
</script>
